import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Contabilidad. */
export const contabilidadSections = [
  functionDoc({
    id: 'cont-compras-diferenciadas',
    name: 'Compras diferenciadas',
    type: 'report',
    route: '/contabilidad/reportes/compras-diferenciadas',
    locationPath: 'Contabilidad → Compras diferenciadas',
    generalDescription:
      'Reporte mensual de documentos de compra (facturas, boletas y equivalentes) clasificados según si el gasto se relaciona con ingreso a almacén (insumos), con servicio/gasto sin almacén o mixto. Permite revisar totales por clasificación y moneda, abrir el detalle de cada comprobante (guías de ingreso, insumos consolidados y secciones sin almacén) y exportar un Excel detallado para contabilización o conciliación en el ERP.',
    prerequisites: [
      'Usuario con rol contador o permiso explícito de Contabilidad (ver menú lateral).',
      'Documentos de compra registrados en Compras → Documentos con fecha de emisión en el mes consultado.',
      'Guías de ingreso vinculadas cuando la clasificación depende de movimiento a almacén (documentos sin guía pueden figurar como gasto/servicio).',
    ],
    interfaceElements: [
      {
        name: 'Migas de pan (Dashboard / Contabilidad / Compras diferenciadas)',
        controlType: 'Enlaces',
        required: false,
        editable: true,
        automatic: true,
        description: 'Navegación superior; Dashboard devuelve al inicio.',
        behavior: 'El enlace Dashboard lleva a /dashboard.',
      },
      {
        name: 'Año',
        controlType: 'Lista desplegable',
        required: true,
        editable: true,
        automatic: false,
        description: 'Año calendario del período a analizar.',
        behavior: 'Muestra el año actual y los siete años anteriores.',
        example: '2026',
      },
      {
        name: 'Mes',
        controlType: 'Lista desplegable',
        required: true,
        editable: true,
        automatic: false,
        description: 'Mes del reporte (nombres en español: Enero… Diciembre).',
        behavior: 'Valor por defecto: mes actual del sistema (zona America/Lima en servidor).',
        example: 'Octubre',
      },
      {
        name: 'Consultar',
        controlType: 'Botón',
        required: true,
        editable: true,
        automatic: false,
        description: 'Carga el reporte para el año y mes seleccionados.',
        behavior: 'Mientras consulta muestra el texto «Consultando…» y deshabilita acciones repetidas. Hasta pulsar Consultar no se muestran tarjetas ni tabla.',
      },
      {
        name: 'Exportar Excel',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Genera y descarga un archivo .xlsx con detalle línea a línea del mes.',
        behavior: 'Puede usarse tras haber consultado; si no hay filas muestra aviso. Durante la exportación muestra «Exportando…». El nombre del archivo incluye año y mes (compras-diferenciadas-detalle-AAAA-MM.xlsx).',
      },
      {
        name: 'Tarjetas de totales por clasificación',
        controlType: 'Panel',
        required: false,
        editable: false,
        automatic: true,
        description: 'Resumen por tipo: con almacén, sin almacén, mixto (etiquetas con color).',
        behavior: 'Muestra monto total en soles por defecto y cantidad de documentos. Si no hay documentos en el mes, un mensaje indica «Sin documentos en [mes] [año]».',
      },
      {
        name: 'Totales por moneda',
        controlType: 'Panel',
        required: false,
        editable: false,
        automatic: true,
        description: 'Suma de totales agrupados por moneda del documento (PEN, USD, etc.).',
        behavior: 'Solo visible si el API devuelve al menos un grupo.',
      },
      {
        name: 'Tabla de documentos',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Listado de comprobantes del mes con clasificación contable.',
        behavior: 'Columnas: #, Código, Tipo, Serie/Núm., Proveedor (y documento), Fecha, Clasificación (insignia), Base, IGV, Total. Clic en una fila abre el modal de detalle.',
      },
      {
        name: 'Modal Detalle',
        controlType: 'Ventana modal',
        required: false,
        editable: false,
        automatic: true,
        description: 'Detalle del documento seleccionado.',
        behavior: 'Secciones: Referencias (guías de ingreso con productos), Insumos consolidado, y otras secciones según datos del API. Botón × o clic fuera cierra el modal.',
      },
      {
        name: 'Mensajes toast',
        controlType: 'Notificación',
        required: false,
        editable: false,
        automatic: true,
        description: 'Errores de carga, exportación o detalle; éxito al exportar con cantidad de filas.',
        behavior: 'Se cierra manualmente o al disparar otra acción.',
      },
    ],
    procedure: [
      'Inicie sesión con un usuario contador o con acceso a Contabilidad; si su rol lo define, puede entrar directamente a esta pantalla tras el login.',
      'En el menú lateral abra Contabilidad y seleccione Compras diferenciadas.',
      'En el panel de filtros elija el Año y el Mes que desea revisar (por defecto aparece el mes en curso).',
      'Pulse Consultar y espere a que termine la carga.',
      'Revise las tarjetas de totales por clasificación y, si aparece, el bloque Totales por moneda.',
      'Recorra la tabla: use el código subrayado o haga clic en la fila para abrir el detalle (guías, insumos y montos).',
      'Cierre el detalle con × cuando termine la revisión.',
      'Si necesita trabajar en Excel, pulse Exportar Excel y abra el archivo descargado en su hoja de cálculo.',
    ],
    practicalExample: {
      context: 'Cierre contable de compras de octubre con documentos en almacén y servicios.',
      data: {
        Año: 2026,
        Mes: 'Octubre',
        Documento_ejemplo: 'FAC-2026-00123',
        Clasificación: 'Con almacén (insumos con guía de ingreso)',
      },
      steps: [
        'Seleccione Año 2026 y Mes Octubre.',
        'Pulse Consultar; verifique que la tarjeta «Con almacén» coincida con las facturas que tienen guía en Compras.',
        'Abra el detalle de FAC-2026-00123 y confirme que las guías listadas son las mismas que en Almacenes.',
        'Exporte Excel y filtre en la columna Clasificación para separar gasto operativo sin almacén.',
      ],
    },
    validations: [
      'Debe elegir año y mes antes de Consultar (los selectores siempre tienen valor).',
      'La exportación sin filas en el mes muestra advertencia «No hay datos para exportar».',
      'Documentos sin guías asociadas pueden clasificarse como sin almacén; valide en Compras antes de asentar.',
      'Montos se muestran en la moneda del documento (S/ o US$ según corresponda).',
    ],
    expectedResult: [
      'Tras Consultar, aparecen tarjetas de resumen y la tabla con todos los documentos del mes, o un mensaje central si no hay registros.',
      'El detalle modal muestra código, tipo, serie/número, proveedor y desglose de guías e insumos.',
      'El Excel descargado incluye título del mes, encabezados con autofiltro y columnas de montos, base, IGV y moneda.',
    ],
    commonErrors: [
      {
        error: 'Error al cargar el reporte',
        cause: 'Fallo de red, sesión expirada o error del servidor.',
        solution: 'Recargue la página, vuelva a iniciar sesión si recibe 401 y reintente Consultar.',
      },
      {
        error: 'Error al cargar el detalle',
        cause: 'Documento eliminado o sin permiso sobre el registro.',
        solution: 'Verifique que el documento siga en Compras → Documentos e informe a soporte si persiste.',
      },
      {
        error: 'No hay datos para exportar',
        cause: 'Mes sin documentos o consulta no ejecutada.',
        solution: 'Pulse Consultar primero; confirme que existan comprobantes con fecha de emisión en ese mes.',
      },
      {
        error: 'No veo el menú Contabilidad',
        cause: 'Rol sin acceso contable o módulo no habilitado para su perfil.',
        solution: 'Solicite al administrador rol contador o permiso de contabilidad en Administrador → Usuarios.',
      },
    ],
    relatedFunctions: [
      'compras-documentos-list',
      'adm-resultados',
      'Documentos de compra y guías de ingreso en módulos Compras y Almacenes.',
    ],
    observations: [
      ROLES.contador,
      'La clasificación «mixto» indica comprobantes con partes con y sin almacén; revise siempre el detalle antes del asiento.',
      'Insertar captura: pantalla principal con filtros y tabla — ' + SHOTS.listado,
      'Insertar captura: modal de detalle con guías e insumos — ' + SHOTS.vistaGeneral,
    ],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.contador,
  }),
];
